import {
	getTextureFormat,
	getTextureInternalFormat,
	getTextureType,
	TextureFormat,
} from "@/core/backends/webgl/texture-format"
import { getBytesPerPixel } from "@/core/backends/webgl/texture-size"
import { uuid } from "@/utils/uuid"
import { WebGLBackend } from "../webgl-backend"
import { AsyncQueueLock } from "@/utils/lock"

export type TextureFilter = "linear" | "nearest"
export type TextureWrap = "repeat" | "clamp-to-edge"

export interface TextureOptions extends TextureDataOptions {
	label: string
	mips?: boolean
	min?: TextureFilter
	mag?: TextureFilter
	wrapS?: TextureWrap
	wrapT?: TextureWrap
}

export interface TextureDataOptions {
	width?: number
	height?: number
	format?: TextureFormat
	data?: (TexImageSource & { width: number; height: number }) | ArrayBufferView | null
	flipY?: boolean
	premultiplyAlpha?: boolean
	rowLength?: number
	skipRows?: number
	skipCols?: number
}

export class Texture {
	static framebuffer: WebGLFramebuffer | null = null
	static readFramebuffer: WebGLFramebuffer | null = null
	static readbackLock = new AsyncQueueLock()

	private backend: WebGLBackend

	private get gl() {
		return this.backend.gl
	}

	private texture: WebGLTexture
	private initialOptions: TextureOptions

	get nativeTexture() {
		return this.texture
	}

	id: string = ""
	label: string

	format: TextureFormat
	width: number
	height: number

	constructor(backend: WebGLBackend, options: TextureOptions = { label: "untitledTexture" }) {
		this.id = uuid()
		this.backend = backend

		this.texture = createTexture(this.gl, options)
		this.format = options.format ?? "rgba8unorm"
		this.label = options.label

		const { data, ...rest } = options
		this.initialOptions = { ...rest, data: null }

		if (ArrayBuffer.isView(data) || data == null) {
			this.width = options.width ?? 0
			this.height = options.height ?? 0
		} else if (typeof HTMLImageElement !== "undefined" && data instanceof HTMLImageElement) {
			this.width = data.naturalWidth
			this.height = data.naturalHeight
		} else {
			this.width = data.width
			this.height = data.height
		}

		this.backend.textureMemoryCounters.add(this)
	}

	swapWith(other: Texture) {
		const tempTexture = other.texture
		const tempWidth = other.width
		const tempHeight = other.height

		other.texture = this.texture
		other.width = this.width
		other.height = this.height

		this.texture = tempTexture
		this.width = tempWidth
		this.height = tempHeight
	}

	bind(unit: number) {
		this.gl.activeTexture(this.gl.TEXTURE0 + unit)
		this.gl.bindTexture(this.gl.TEXTURE_2D, this.texture)

		this.backend.textureMemoryCounters.update(this)
	}

	unbind(unit: number) {
		this.gl.activeTexture(this.gl.TEXTURE0 + unit)
		this.gl.bindTexture(this.gl.TEXTURE_2D, null)
	}

	bindFramebuffer(attachments?: Texture[]) {
		Texture.framebuffer = Texture.framebuffer || this.gl.createFramebuffer()

		this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, Texture.framebuffer)
		this.gl.framebufferTexture2D(
			this.gl.FRAMEBUFFER,
			this.gl.COLOR_ATTACHMENT0,
			this.gl.TEXTURE_2D,
			this.texture,
			0,
		)

		if (attachments) {
			const attachmentBuffers: number[] = [this.gl.COLOR_ATTACHMENT0]

			// Only allow up to 4 attachments
			for (let i = 0; i < 4; i++) {
				const attatchment = attachments[i]

				if (!attatchment) {
					continue
				}

				const attachmentBuffer = this.gl.COLOR_ATTACHMENT0 + (i + 1)

				attachmentBuffers.push(attachmentBuffer)

				this.gl.framebufferTexture2D(
					this.gl.FRAMEBUFFER,
					attachmentBuffer,
					this.gl.TEXTURE_2D,
					attatchment.texture,
					0,
				)
			}

			this.gl.drawBuffers(attachmentBuffers)
		} else {
			// unbind attachments
			for (let i = 0; i < 4; i++) {
				this.gl.framebufferTexture2D(
					this.gl.FRAMEBUFFER,
					this.gl.COLOR_ATTACHMENT0 + (i + 1),
					this.gl.TEXTURE_2D,
					null,
					0,
				)
			}

			this.gl.drawBuffers([this.gl.COLOR_ATTACHMENT0])
		}

		if (import.meta.env.DEV) {
			if (
				this.gl.checkFramebufferStatus(this.gl.FRAMEBUFFER) != this.gl.FRAMEBUFFER_COMPLETE
			) {
				console.warn("Rendering to this texture is not supported:", this)
				throw new Error(
					`Rendering to this texture is not supported (incomplete framebuffer):`,
				)
			}
		}

		this.gl.viewport(0, 0, this.width, this.height)
	}

	bindReadFramebuffer() {
		Texture.readFramebuffer = Texture.readFramebuffer || this.gl.createFramebuffer()

		this.gl.bindFramebuffer(this.gl.READ_FRAMEBUFFER, Texture.readFramebuffer)
		this.gl.framebufferTexture2D(
			this.gl.READ_FRAMEBUFFER,
			this.gl.COLOR_ATTACHMENT0,
			this.gl.TEXTURE_2D,
			this.texture,
			0,
		)

		if (import.meta.env.DEV) {
			if (
				this.gl.checkFramebufferStatus(this.gl.FRAMEBUFFER) != this.gl.FRAMEBUFFER_COMPLETE
			) {
				throw new Error("Reading this texture is not supported (incomplete framebuffer)")
			}
		}

		this.gl.viewport(0, 0, this.width, this.height)
	}

	bindDrawFramebuffer() {
		Texture.framebuffer = Texture.framebuffer || this.gl.createFramebuffer()

		this.gl.bindFramebuffer(this.gl.DRAW_FRAMEBUFFER, Texture.framebuffer)
		this.gl.framebufferTexture2D(
			this.gl.DRAW_FRAMEBUFFER,
			this.gl.COLOR_ATTACHMENT0,
			this.gl.TEXTURE_2D,
			this.texture,
			0,
		)

		if (import.meta.env.DEV) {
			if (
				this.gl.checkFramebufferStatus(this.gl.FRAMEBUFFER) != this.gl.FRAMEBUFFER_COMPLETE
			) {
				throw new Error("Drawing to this texture is not supported (incomplete framebuffer)")
			}
		}

		this.gl.viewport(0, 0, this.width, this.height)
	}

	update(options: TextureDataOptions) {
		this.width = options.width ?? this.width
		this.height = options.height ?? this.height
		this.format = options.format ?? this.format

		this.gl.activeTexture(this.gl.TEXTURE0)
		this.gl.bindTexture(this.gl.TEXTURE_2D, this.texture)

		setTextureData(this.gl, {
			width: this.width,
			height: this.height,
			format: this.format,
			...options,
		})

		this.backend.textureMemoryCounters.update(this)
	}

	updateLabel(label: string) {
		this.label = label
	}

	updateSize(width: number, height: number) {
		width = Math.floor(width)
		height = Math.floor(height)

		if (width === this.width && height === this.height) {
			return
		}

		this.width = width
		this.height = height

		const internalFormat = getTextureInternalFormat(this.gl, this.format)
		const format = getTextureFormat(this.gl, this.format)
		const type = getTextureType(this.gl, this.format)

		this.gl.activeTexture(this.gl.TEXTURE0)
		this.gl.bindTexture(this.gl.TEXTURE_2D, this.texture)
		this.gl.texImage2D(
			this.gl.TEXTURE_2D,
			0,
			internalFormat,
			width,
			height,
			0,
			format,
			type,
			null,
		)

		this.backend.textureMemoryCounters.update(this)
	}

	generateMipmap() {
		this.gl.bindTexture(this.gl.TEXTURE_2D, this.texture)
		this.gl.generateMipmap(this.gl.TEXTURE_2D)
	}

	getBytesPerPixel() {
		const internalFormat = getTextureInternalFormat(this.gl, this.format)
		return getBytesPerPixel(this.gl, internalFormat)
	}

	getByteLength() {
		const size = this.width * this.height
		return size * this.getBytesPerPixel()
	}

	getBufferView(
		options: {
			buffer?: ArrayBuffer
			offset?: number
		} = {},
	) {
		const type = getTextureType(this.gl, this.format)
		const byteLength = this.getByteLength()
		const byteOffset = options.offset ?? 0

		const buffer = options.buffer ?? new ArrayBuffer(byteLength)

		switch (type) {
			case this.gl.FLOAT:
				return new Float32Array(buffer, byteOffset, byteLength >> 2)
			case this.gl.UNSIGNED_SHORT:
				return new Uint16Array(buffer, byteOffset, byteLength >> 1)
			default:
				return new Uint8Array(buffer, byteOffset, byteLength)
		}
	}

	read(options?: {
		bufferView?: Uint8Array | Uint16Array | Float32Array
		packAlignment?: number
	}) {
		Texture.framebuffer = Texture.framebuffer || this.gl.createFramebuffer()

		const type = getTextureType(this.gl, this.format)
		const format = getTextureFormat(this.gl, this.format)

		const pixels = this.getBufferView({
			buffer: options?.bufferView?.buffer as ArrayBuffer | undefined,
			offset: options?.bufferView?.byteOffset,
		})

		this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, Texture.framebuffer)
		this.gl.framebufferTexture2D(
			this.gl.FRAMEBUFFER,
			this.gl.COLOR_ATTACHMENT0,
			this.gl.TEXTURE_2D,
			this.texture,
			0,
		)

		this.gl.pixelStorei(this.gl.PACK_ALIGNMENT, options?.packAlignment ?? 4)

		this.gl.readPixels(0, 0, this.width, this.height, format, type, pixels)
		this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, null)

		return pixels
	}

	async readAsync(options?: {
		bufferView?: Uint8Array | Uint16Array | Float32Array
		packAlignment?: number
		pollIntervalMs?: number
	}): Promise<Uint8Array | Uint16Array | Float32Array> {
		const gl = this.gl
		if (!(gl instanceof WebGL2RenderingContext)) throw new Error("WebGL2 required")

		Texture.framebuffer = Texture.framebuffer || gl.createFramebuffer()
		const type = getTextureType(gl, this.format)
		const format = getTextureFormat(gl, this.format)

		const pixels = this.getBufferView({
			buffer: options?.bufferView?.buffer as ArrayBuffer | undefined,
			offset: options?.bufferView?.byteOffset,
		})

		// Buffer critical area. The shared framebuffer and PIXEL_PACK_BUFFER
		// bindings must not be interleaved across concurrent readAsync calls.
		await Texture.readbackLock.lock()

		const pbo = gl.createBuffer()

		if (!pbo) {
			Texture.readbackLock.unlock()
			throw new Error("Failed to create PIXEL_PACK_BUFFER")
		}

		let sync: WebGLSync | null = null

		try {
			gl.bindFramebuffer(gl.FRAMEBUFFER, Texture.framebuffer)
			gl.framebufferTexture2D(
				gl.FRAMEBUFFER,
				gl.COLOR_ATTACHMENT0,
				gl.TEXTURE_2D,
				this.texture,
				0,
			)

			gl.pixelStorei(gl.PACK_ALIGNMENT, options?.packAlignment ?? 4)
			gl.bindBuffer(gl.PIXEL_PACK_BUFFER, pbo)
			gl.bufferData(gl.PIXEL_PACK_BUFFER, pixels.byteLength, gl.STREAM_READ)
			gl.readPixels(0, 0, this.width, this.height, format, type, 0)
			gl.bindBuffer(gl.PIXEL_PACK_BUFFER, null)
			gl.bindFramebuffer(gl.FRAMEBUFFER, null)

			sync = gl.fenceSync(gl.SYNC_GPU_COMMANDS_COMPLETE, 0)
			if (!sync) throw new Error("Failed to create GPU sync")
			gl.flush()
		} catch (error) {
			gl.bindBuffer(gl.PIXEL_PACK_BUFFER, null)
			gl.bindFramebuffer(gl.FRAMEBUFFER, null)
			gl.deleteBuffer(pbo)
			throw error
		} finally {
			Texture.readbackLock.unlock()
		}

		const poll = options?.pollIntervalMs ?? 10

		await new Promise<void>((resolve, reject) => {
			const check = () => {
				const status = gl.clientWaitSync(sync, 0, 0)
				if (status === gl.WAIT_FAILED) return reject(new Error("clientWaitSync failed"))
				if (status === gl.TIMEOUT_EXPIRED) return setTimeout(check, poll)
				resolve()
			}
			check()
		})

		gl.deleteSync(sync)

		// Buffer critical area
		await Texture.readbackLock.lock()

		gl.bindBuffer(gl.PIXEL_PACK_BUFFER, pbo)
		gl.getBufferSubData(gl.PIXEL_PACK_BUFFER, 0, pixels)
		gl.bindBuffer(gl.PIXEL_PACK_BUFFER, null)
		gl.deleteBuffer(pbo)

		Texture.readbackLock.unlock()

		return pixels
	}

	// Delete texture from memory and create a new (fresh) one
	reset() {
		this.delete()
		this.texture = createTexture(this.gl, { ...this.initialOptions, width: 0, height: 0 })
		this.updateSize(0, 0)

		this.backend.textureMemoryCounters.add(this)
	}

	delete() {
		this.gl.deleteTexture(this.texture)

		this.backend.textureMemoryCounters.delete(this)
	}
}

function createTexture(
	gl: WebGL2RenderingContext,
	options: TextureOptions = { label: "untitledTexture" },
) {
	const texture = gl.createTexture()

	if (texture == null) {
		throw new Error("Could not create texture")
	}

	const minFilter = getTextureFilter(options.min ?? "linear", options.mips)
	const magFilter = getTextureFilter(options.mag ?? "linear", false)
	const wrapS = getTextureWrap(options.wrapS ?? "clamp-to-edge")
	const wrapT = getTextureWrap(options.wrapT ?? "clamp-to-edge")

	gl.bindTexture(gl.TEXTURE_2D, texture)
	gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, minFilter)
	gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, magFilter)
	gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, wrapS)
	gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, wrapT)

	setTextureData(gl, options)

	return texture
}

function setTextureData(gl: WebGL2RenderingContext, options: TextureDataOptions) {
	gl.pixelStorei(gl.UNPACK_COLORSPACE_CONVERSION_WEBGL, gl.NONE)
	gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, options.flipY ?? false)
	gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, options.premultiplyAlpha ?? false)
	gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1)
	gl.pixelStorei(gl.UNPACK_SKIP_ROWS, options.skipRows ?? 0)
	gl.pixelStorei(gl.UNPACK_SKIP_PIXELS, options.skipCols ?? 0)
	gl.pixelStorei(gl.UNPACK_ROW_LENGTH, options.rowLength ?? 0)

	const internalFormat = getTextureInternalFormat(gl, options.format)
	const format = getTextureFormat(gl, options.format)
	const type = getTextureType(gl, options.format)

	const width = options.width ?? 0
	const height = options.height ?? 0

	if (ArrayBuffer.isView(options.data) || options.data == null) {
		gl.texImage2D(
			gl.TEXTURE_2D,
			0,
			internalFormat,
			width,
			height,
			0,
			format,
			type,
			options.data ?? null,
		)
	} else if (options.data) {
		gl.texImage2D(gl.TEXTURE_2D, 0, internalFormat, format, type, options.data)
	}
}

export function getTextureFilter(filter: TextureFilter, mips?: boolean) {
	const ctx = WebGL2RenderingContext

	switch (filter) {
		case "linear":
			return mips ? ctx.LINEAR_MIPMAP_LINEAR : ctx.LINEAR
		case "nearest":
			return mips ? ctx.NEAREST_MIPMAP_NEAREST : ctx.NEAREST
	}
}

export function getTextureWrap(wrap: TextureWrap) {
	const ctx = WebGL2RenderingContext

	switch (wrap) {
		case "clamp-to-edge":
			return ctx.CLAMP_TO_EDGE
		case "repeat":
			return ctx.REPEAT
	}
}
